// src/components/icons.tsx
//
// The small glyph set the action buttons share. 16 px, `currentColor`, stroked, `aria-hidden`: a glyph carries NO
// meaning on its own here. The label beside it (or the visually hidden label inside an `IconButton`) is what a
// screen reader announces. These were private to `ExportToolbar`; they live here so the toolbar, the pagination
// and the row actions draw the same print, mail and refresh marks.
import type { ReactElement } from 'react'

export type GlyphName = 'print' | 'pdf' | 'excel' | 'email' | 'refresh' | 'trash' | 'first' | 'last' | 'history' | 'plus'

const common = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  'aria-hidden': true,
  focusable: false,
} as const

const GLYPHS: Readonly<Record<GlyphName, ReactElement>> = {
  print: (
    <svg {...common}>
      <path d="M7 9V4h10v5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="4" y="9" width="16" height="7" rx="1.5" />
      <path d="M7 16h10v4H7z" strokeLinejoin="round" />
    </svg>
  ),
  pdf: (
    <svg {...common}>
      <path d="M6 3h8l4 4v14H6z" strokeLinejoin="round" />
      <path d="M14 3v4h4" strokeLinejoin="round" />
      <path d="M9 13h6M9 17h4" strokeLinecap="round" />
    </svg>
  ),
  excel: (
    <svg {...common}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <path d="M4 10h16M10 4v16" />
    </svg>
  ),
  email: (
    <svg {...common}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  refresh: (
    <svg {...common}>
      <path d="M20 11a8 8 0 0 0-14.3-4.6L4 8.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 4v4.5h4.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 13a8 8 0 0 0 14.3 4.6L20 15.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 20v-4.5h-4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  trash: (
    <svg {...common}>
      <path d="M4 7h16" strokeLinecap="round" />
      <path d="M9 7V4.5h6V7" strokeLinejoin="round" />
      <path d="M6.5 7l.8 12.5h9.4L17.5 7" strokeLinejoin="round" />
      <path d="M10 11v5M14 11v5" strokeLinecap="round" />
    </svg>
  ),
  first: (
    <svg {...common}>
      <path d="M6 5v14" strokeLinecap="round" />
      <path d="m18 6-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  history: (
    <svg {...common}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.5 4v4.5H8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 7.5V12l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  plus: (
    <svg {...common}>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  ),
  last: (
    <svg {...common}>
      <path d="M18 5v14" strokeLinecap="round" />
      <path d="m6 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}

export function Glyph({ name }: { readonly name: GlyphName }): ReactElement {
  return GLYPHS[name]
}
